import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  ClassifiedAd,
  ClassifiedAdImage,
  ClassifiedAdInput,
  ClassifiedInterest,
  ClassifiedInterestStatus,
  ClassifiedListParams,
  ClassifiedPage,
  ClassifiedSpaceConfig,
  ClassifiedSpaceInput,
  MyClassifiedInterest,
  PendingClassifiedAd
} from '@/types/classifieds'

const toPage = <T>(result: unknown): ClassifiedPage<T> => {
  const page = result as Partial<ClassifiedPage<T>> | null
  return { items: listItems<T>(result), pagination: page?.pagination ?? null }
}

/** F-72: classifieds — the space terms, ads, images, interests and the moderation queue. Paths are written by hand (F-03). */
export class ClassifiedsResource {
  constructor(private ctx: SdkContext) {}

  /* ------------------------------------------------------------- space -- */

  async getSpace(spaceId: string): Promise<ClassifiedSpaceConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/classifieds/spaces/{spaceId}' }, { spaceId })
  }

  async updateSpace(spaceId: string, body: ClassifiedSpaceInput): Promise<ClassifiedSpaceConfig> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/classifieds/spaces/{spaceId}', hasBody: true },
      { spaceId, body }
    )
  }

  /* --------------------------------------------------------------- ads -- */

  async listAds(params: ClassifiedListParams): Promise<ClassifiedPage<ClassifiedAd>> {
    const { spaceId, categoryId, q, mine, status, page, limit } = params
    return toPage(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/classifieds/ads' },
        { query: { spaceId, categoryId, q: q?.trim() || undefined, mine: mine || undefined, status, page, limit } }
      )
    )
  }

  async getAd(id: string): Promise<ClassifiedAd> {
    return this.ctx.call({ httpMethod: 'GET', path: '/classifieds/ads/{id}' }, { id })
  }

  async createAd(body: ClassifiedAdInput & { spaceId: string; durationDays: number }): Promise<ClassifiedAd> {
    return this.ctx.call({ httpMethod: 'POST', path: '/classifieds/ads', hasBody: true }, { body })
  }

  async updateAd(id: string, body: Partial<ClassifiedAdInput>): Promise<ClassifiedAd> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/classifieds/ads/{id}', hasBody: true }, { id, body })
  }

  async deleteAd(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/classifieds/ads/{id}' }, { id })
  }

  /* ------------------------------------------------------------ images -- */

  /** Multipart, field `images`. The whole batch is validated before anything is stored. */
  async uploadImages(adId: string, files: File[]): Promise<ClassifiedAdImage[]> {
    const body = new FormData()
    files.forEach((file) => body.append('images', file))
    return listItems<ClassifiedAdImage>(
      await this.ctx.raw(
        { httpMethod: 'POST', path: '/upload/classifieds/ads/{adId}/images', hasBody: true },
        { adId, body }
      )
    )
  }

  /** F-76: attaches confirmed `classifiedImage` media to the ad (6 images at most, counting the old ones). */
  async addMediaImages(adId: string, mediaIds: string[]): Promise<ClassifiedAdImage[]> {
    return listItems<ClassifiedAdImage>(
      await this.ctx.raw(
        { httpMethod: 'POST', path: '/classifieds/ads/{adId}/images/media', hasBody: true },
        { adId, body: { mediaIds } }
      )
    )
  }

  async deleteImage(adId: string, imageId: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/upload/classifieds/ads/{adId}/images/{imageId}' },
      { adId, imageId }
    )
  }

  /* --------------------------------------------------------- interests -- */

  async createInterest(adId: string, message: string): Promise<ClassifiedInterest> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/classifieds/ads/{id}/interests', hasBody: true },
      { id: adId, body: { message } }
    )
  }

  async withdrawInterest(adId: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/classifieds/ads/{id}/interests/mine' }, { id: adId })
  }

  async listInterests(adId: string, page = 1, limit = 50): Promise<ClassifiedPage<ClassifiedInterest>> {
    return toPage(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/classifieds/ads/{id}/interests' },
        { id: adId, query: { page, limit } }
      )
    )
  }

  async setInterestStatus(
    adId: string,
    interestId: string,
    status: Exclude<ClassifiedInterestStatus, 'new'>
  ): Promise<ClassifiedInterest> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/classifieds/ads/{id}/interests/{interestId}', hasBody: true },
      { id: adId, interestId, body: { status } }
    )
  }

  async listMyInterests(page = 1, limit = 50): Promise<ClassifiedPage<MyClassifiedInterest>> {
    return toPage(
      await this.ctx.raw({ httpMethod: 'GET', path: '/classifieds/interests/mine' }, { query: { page, limit } })
    )
  }

  /* -------------------------------------------------------- moderation -- */

  async listPending(page = 1, limit = 50): Promise<ClassifiedPage<PendingClassifiedAd>> {
    return toPage(
      await this.ctx.raw({ httpMethod: 'GET', path: '/moderation/classifieds/pending' }, { query: { page, limit } })
    )
  }

  async moderate(id: string, status: 'APPROVED' | 'REJECTED', rejectionReason?: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/moderation/classifieds/{id}', hasBody: true },
      { id, body: { status, ...(rejectionReason ? { rejectionReason } : {}) } }
    )
  }
}
